import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  Award, Users, TrendingUp, AlertTriangle, Plus, Edit2, Trash2, X,
  ChevronDown, Star, CheckCircle, Clock, Activity, BarChart2,
  Search, Filter, RefreshCw, Download, Calendar, Loader2,
  BookOpen, Target, Zap, GraduationCap, AlertCircle, CheckCheck,
  UserCheck, Clipboard, Eye, ArrowUp, ArrowDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { YearContext } from '../components/Layout/Layout';
import { enrichmentAPI } from '../services/api';

// ── Constants ────────────────────────────────────────────────
const CATEGORIES = ['Enrichment','Remedial','Olympiad','Language Lab','Coding','STEM','Sports Academy','Music','Public Speaking','Other'];
const TERMS      = ['Term 1','Term 2','Term 3','Annual'];
const ASSESS_TYPES = ['baseline','formative','summative','final'];

const PERF_COLORS = {
  'Outstanding':            { bg:'bg-emerald-50',  text:'text-emerald-700', bar:'#10b981', ring:'ring-emerald-300' },
  'Excellent':              { bg:'bg-blue-50',     text:'text-blue-700',    bar:'#3b82f6', ring:'ring-blue-300'    },
  'Good':                   { bg:'bg-amber-50',    text:'text-amber-700',   bar:'#f59e0b', ring:'ring-amber-300'   },
  'Needs Improvement':      { bg:'bg-orange-50',   text:'text-orange-700',  bar:'#f97316', ring:'ring-orange-300'  },
  'Critical Intervention':  { bg:'bg-red-50',      text:'text-red-700',     bar:'#ef4444', ring:'ring-red-300'     },
};
const PIE_COLORS = ['#10b981','#3b82f6','#f59e0b','#f97316','#ef4444'];

function perfColor(level) { return PERF_COLORS[level] || { bg:'bg-gray-100',text:'text-gray-500',bar:'#94a3b8',ring:'ring-gray-200' }; }
function growthLevel(pct) {
  if (pct == null) return null;
  if (pct >= 90) return 'Outstanding';
  if (pct >= 75) return 'Excellent';
  if (pct >= 60) return 'Good';
  if (pct >= 40) return 'Needs Improvement';
  return 'Critical Intervention';
}

// ── Shared UI ─────────────────────────────────────────────────
function Modal({ title, onClose, children, wide, xl }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-10 overflow-y-auto">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${xl?'max-w-5xl':wide?'max-w-3xl':'max-w-lg'} flex flex-col`} onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
        </div>
        <div className="px-6 py-5 overflow-y-auto max-h-[80vh]">{children}</div>
      </div>
    </div>
  );
}

const inp   = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400';
const btn   = (v='primary') => {
  const m = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white',
    outline: 'border border-gray-200 hover:bg-gray-50 text-gray-700',
    danger:  'border border-red-200 text-red-600 hover:bg-red-50',
    green:   'bg-emerald-600 hover:bg-emerald-700 text-white',
    amber:   'bg-amber-500 hover:bg-amber-600 text-white',
  };
  return `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${m[v]}`;
};
function SL({ children }) { return <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{children}</label>; }
function F({ label, children, half }) { return <div className={`mb-4 ${half?'':'w-full'}`}><SL>{label}</SL>{children}</div>; }
function KPICard({ label, value, sub, icon: Icon, color='blue', trend }) {
  const c = { blue:'from-blue-500 to-blue-600', emerald:'from-emerald-500 to-emerald-600', amber:'from-amber-500 to-amber-600', red:'from-red-500 to-red-600', purple:'from-purple-500 to-purple-600' };
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${c[color]} flex items-center justify-center mb-3`}>
        <Icon size={18} className="text-white"/>
      </div>
      <p className="text-2xl font-black text-gray-900">{value ?? '—'}</p>
      <p className="text-sm font-medium text-gray-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      {trend != null && (
        <p className={`text-xs font-semibold mt-1 flex items-center gap-0.5 ${trend>=0?'text-emerald-600':'text-red-500'}`}>
          {trend>=0?<ArrowUp size={11}/>:<ArrowDown size={11}/>}{Math.abs(trend)}%
        </p>
      )}
    </div>
  );
}
function PerfBadge({ level, small }) {
  const c = perfColor(level);
  return (
    <span className={`inline-flex items-center font-semibold rounded-full ring-1 ${c.bg} ${c.text} ${c.ring} ${small?'text-xs px-2 py-0.5':'text-sm px-3 py-1'}`}>
      {level||'—'}
    </span>
  );
}
function GrowthBar({ pct }) {
  if (pct == null) return <span className="text-gray-400 text-sm">—</span>;
  const lvl = growthLevel(pct);
  const col = perfColor(lvl).bar;
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 bg-gray-100 rounded-full h-2">
        <div className="h-2 rounded-full transition-all" style={{ width:`${Math.min(100,Math.max(0,pct))}%`, backgroundColor:col }}/>
      </div>
      <span className="text-xs font-bold w-10 text-right" style={{ color:col }}>{pct}%</span>
    </div>
  );
}

// ── Programme Modal ────────────────────────────────────────────
function ProgrammeModal({ prog, employees, grades, yearId, onSave, onClose }) {
  const [form, setForm] = useState({
    name: prog?.name||'', category: prog?.category||'Enrichment',
    subject: prog?.subject||'', academic_year_id: prog?.academic_year_id||yearId||3,
    term: prog?.term||'', coordinator_id: prog?.coordinator_id||'',
    teacher_id: prog?.teacher_id||'',
    target_grades: prog ? (Array.isArray(prog.target_grades)?prog.target_grades:JSON.parse(prog.target_grades||'[]')) : [],
    start_date: prog?.start_date?.slice(0,10)||'', end_date: prog?.end_date?.slice(0,10)||'',
    description: prog?.description||'', status: prog?.status||'active',
  });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const toggleGrade = g => set('target_grades', form.target_grades.includes(g) ? form.target_grades.filter(x=>x!==g) : [...form.target_grades,g]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Programme name is required');
    setSaving(true);
    try {
      if (prog?.id) await enrichmentAPI.updateProgramme(prog.id, form);
      else await enrichmentAPI.createProgramme(form);
      toast.success(prog?.id?'Updated':'Programme created');
      onSave();
    } catch(e) { toast.error(e.response?.data?.error||'Save failed'); }
    setSaving(false);
  };

  return (
    <Modal title={prog?.id?'Edit Programme':'New Programme'} onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2"><F label="Programme Name"><input className={inp} value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. English Enrichment Term 1"/></F></div>
        <F label="Category">
          <select className={inp} value={form.category} onChange={e=>set('category',e.target.value)}>
            {CATEGORIES.map(c=><option key={c}>{c}</option>)}
          </select>
        </F>
        <F label="Subject"><input className={inp} value={form.subject} onChange={e=>set('subject',e.target.value)} placeholder="English, Math…"/></F>
        <F label="Term">
          <select className={inp} value={form.term} onChange={e=>set('term',e.target.value)}>
            <option value="">Select term</option>
            {TERMS.map(t=><option key={t}>{t}</option>)}
          </select>
        </F>
        <F label="Status">
          <select className={inp} value={form.status} onChange={e=>set('status',e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="completed">Completed</option>
          </select>
        </F>
        <F label="Coordinator">
          <select className={inp} value={form.coordinator_id} onChange={e=>set('coordinator_id',e.target.value)}>
            <option value="">Select coordinator</option>
            {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </F>
        <F label="Teacher">
          <select className={inp} value={form.teacher_id} onChange={e=>set('teacher_id',e.target.value)}>
            <option value="">Select teacher</option>
            {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </F>
        <F label="Start Date"><input type="date" className={inp} value={form.start_date} onChange={e=>set('start_date',e.target.value)}/></F>
        <F label="End Date"><input type="date" className={inp} value={form.end_date} onChange={e=>set('end_date',e.target.value)}/></F>
      </div>
      <F label="Target Grades">
        <div className="flex flex-wrap gap-2 mt-1">
          {grades.map(g=>(
            <button key={g} type="button" onClick={()=>toggleGrade(g)}
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-all ${form.target_grades.includes(g)?'bg-blue-600 text-white border-blue-600':'bg-white text-gray-600 border-gray-200 hover:border-blue-400'}`}>
              {g}
            </button>
          ))}
        </div>
      </F>
      <F label="Description"><textarea className={inp} rows={2} value={form.description} onChange={e=>set('description',e.target.value)} placeholder="Programme goals and details…"/></F>
      <div className="flex justify-end gap-3 mt-2">
        <button className={btn('outline')} onClick={onClose}>Cancel</button>
        <button className={btn()} onClick={submit} disabled={saving}>{saving?'Saving…':prog?.id?'Save Changes':'Create'}</button>
      </div>
    </Modal>
  );
}

// ── Enroll Modal ───────────────────────────────────────────────
function EnrollModal({ programme, employees, yearId, onSave, onClose }) {
  const [students, setStudents] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [selected, setSelected] = useState([]);
  const [filter,   setFilter]   = useState({ grade:'', section:'', q:'' });
  const [teacherId, setTeacherId] = useState(programme.teacher_id||'');

  const loadStudents = useCallback(async () => {
    setLoading(true);
    try {
      const r = await enrichmentAPI.getStudents({ grade:filter.grade||undefined, section:filter.section||undefined, year_id:yearId, programme_id:programme.id });
      setStudents(r.data);
    } catch { toast.error('Failed to load students'); }
    setLoading(false);
  }, [filter.grade, filter.section, yearId, programme.id]);

  useEffect(() => { loadStudents(); }, [loadStudents]);

  const filtered = students.filter(s => !filter.q || s.student_name.toLowerCase().includes(filter.q.toLowerCase()) || s.admission_no?.includes(filter.q));
  const unenrolled = filtered.filter(s => !s.enrolled);
  const toggleAll = () => setSelected(selected.length === unenrolled.length ? [] : unenrolled.map(s=>s.student_id));
  const toggle    = id => setSelected(p => p.includes(id) ? p.filter(x=>x!==id) : [...p,id]);

  const submit = async () => {
    if (!selected.length) return toast.error('Select at least one student');
    const toEnroll = students.filter(s => selected.includes(s.student_id));
    try {
      await enrichmentAPI.enroll(programme.id, { students: toEnroll, teacher_id: teacherId||null });
      toast.success(`${toEnroll.length} students enrolled`);
      onSave();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
  };

  const grades   = [...new Set(students.map(s=>s.grade).filter(Boolean))].sort();
  const sections = [...new Set(students.filter(s=>!filter.grade||s.grade===filter.grade).map(s=>s.section).filter(Boolean))].sort();

  return (
    <Modal title={`Enroll Students — ${programme.name}`} onClose={onClose} xl>
      <div className="flex gap-3 mb-4 flex-wrap">
        <select className={`${inp} w-32`} value={filter.grade} onChange={e=>setFilter(p=>({...p,grade:e.target.value,section:''}))}>
          <option value="">All Grades</option>
          {grades.map(g=><option key={g}>{g}</option>)}
        </select>
        <select className={`${inp} w-32`} value={filter.section} onChange={e=>setFilter(p=>({...p,section:e.target.value}))}>
          <option value="">All Sections</option>
          {sections.map(s=><option key={s}>{s}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input className={`${inp} pl-8`} placeholder="Search name or admission no…" value={filter.q} onChange={e=>setFilter(p=>({...p,q:e.target.value}))}/>
        </div>
        <select className={`${inp} w-52`} value={teacherId} onChange={e=>setTeacherId(e.target.value)}>
          <option value="">Assign Teacher (optional)</option>
          {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="flex justify-center py-10 text-gray-400 gap-2"><Loader2 size={18} className="animate-spin"/> Loading…</div>
      ) : (
        <div className="overflow-auto max-h-[50vh] rounded-xl border border-gray-100">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left w-8">
                  <input type="checkbox" checked={selected.length===unenrolled.length&&unenrolled.length>0} onChange={toggleAll}/>
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-xs uppercase">Student</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-xs uppercase">Adm. No</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-xs uppercase">Class</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-xs uppercase">Teacher</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 text-xs uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s=>(
                <tr key={s.student_id} className={`border-t border-gray-50 ${s.enrolled?'opacity-50':''}`}>
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={selected.includes(s.student_id)} onChange={()=>toggle(s.student_id)} disabled={s.enrolled}/>
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-900">{s.student_name}</td>
                  <td className="px-3 py-2 text-gray-500">{s.admission_no}</td>
                  <td className="px-3 py-2 text-gray-500">{s.class_name}</td>
                  <td className="px-3 py-2 text-gray-500">{s.class_teacher||'—'}</td>
                  <td className="px-3 py-2">
                    {s.enrolled
                      ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Enrolled</span>
                      : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Available</span>}
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={6} className="text-center py-8 text-gray-400">No students found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center justify-between mt-4">
        <p className="text-sm text-gray-500">{selected.length} selected</p>
        <div className="flex gap-3">
          <button className={btn('outline')} onClick={onClose}>Cancel</button>
          <button className={btn()} onClick={submit} disabled={!selected.length}>Enroll {selected.length||''} Students</button>
        </div>
      </div>
    </Modal>
  );
}

// ── Attendance Modal (mark per session) ───────────────────────
function AttendanceModal({ session, programmeId, onSave, onClose }) {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    enrichmentAPI.getSessionAtt(session.id, programmeId)
      .then(r => setRows(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [session.id, programmeId]);

  const toggle = sid => setRows(p => p.map(r => r.student_id===sid ? {...r, is_present: r.is_present?0:1} : r));
  const markAll = v  => setRows(p => p.map(r => ({...r, is_present: v})));

  const save = async () => {
    setSaving(true);
    try {
      await enrichmentAPI.markAttendance(session.id, { programme_id: programmeId, attendance: rows });
      toast.success('Attendance saved');
      onSave();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const present = rows.filter(r=>r.is_present).length;

  return (
    <Modal title={`Attendance — ${session.session_date} ${session.topic?`(${session.topic})`:''}`} onClose={onClose} wide>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin text-gray-400"/></div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-sm text-gray-500">{present}/{rows.length} Present</span>
            <button className={btn('green')} onClick={()=>markAll(1)}>Mark All Present</button>
            <button className={btn('outline')} onClick={()=>markAll(0)}>Mark All Absent</button>
          </div>
          <div className="overflow-auto max-h-[55vh] rounded-xl border border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Student</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Class</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Present</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r=>(
                  <tr key={r.student_id} className="border-t border-gray-50 hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900">{r.student_name}</td>
                    <td className="px-3 py-2 text-gray-500">{r.class_name}</td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={()=>toggle(r.student_id)}
                        className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center mx-auto transition-all ${r.is_present?'bg-emerald-500 border-emerald-500 text-white':'bg-white border-gray-200 text-gray-300'}`}>
                        {r.is_present ? <CheckCheck size={14}/> : ''}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button className={btn('outline')} onClick={onClose}>Cancel</button>
            <button className={btn('green')} onClick={save} disabled={saving}>{saving?'Saving…':'Save Attendance'}</button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── Assessment Template Modal ──────────────────────────────────
function TemplateModal({ programmeId, grades, onSave, onClose }) {
  const [form, setForm] = useState({ name:'', assessment_type:'baseline', grade:'', subject:'', max_marks:100, assessment_date:'', rubric:[] });
  const [rubricInput, setRubricInput] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const addRubric = () => {
    if (!rubricInput.trim()) return;
    setForm(p=>({...p, rubric:[...p.rubric, rubricInput.trim()]}));
    setRubricInput('');
  };
  const removeRubric = i => setForm(p=>({...p, rubric:p.rubric.filter((_,j)=>j!==i)}));

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      await enrichmentAPI.createTemplate(programmeId, { ...form, programme_id: programmeId });
      toast.success('Template created');
      onSave();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    setSaving(false);
  };

  return (
    <Modal title="New Assessment Template" onClose={onClose}>
      <F label="Assessment Name"><input className={inp} value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. Baseline Assessment – Term 1"/></F>
      <div className="grid grid-cols-2 gap-3">
        <F label="Type">
          <select className={inp} value={form.assessment_type} onChange={e=>set('assessment_type',e.target.value)}>
            {ASSESS_TYPES.map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
          </select>
        </F>
        <F label="Max Marks"><input type="number" className={inp} value={form.max_marks} onChange={e=>set('max_marks',e.target.value)} min={1}/></F>
        <F label="Grade">
          <select className={inp} value={form.grade} onChange={e=>set('grade',e.target.value)}>
            <option value="">All Grades</option>
            {grades.map(g=><option key={g}>{g}</option>)}
          </select>
        </F>
        <F label="Assessment Date"><input type="date" className={inp} value={form.assessment_date} onChange={e=>set('assessment_date',e.target.value)}/></F>
      </div>
      <F label="Subject"><input className={inp} value={form.subject} onChange={e=>set('subject',e.target.value)} placeholder="Reading, Writing…"/></F>
      <F label="Rubric / Sub-areas">
        <div className="flex gap-2 mb-2">
          <input className={`${inp} flex-1`} value={rubricInput} onChange={e=>setRubricInput(e.target.value)} placeholder="e.g. Reading Comprehension" onKeyDown={e=>e.key==='Enter'&&addRubric()}/>
          <button className={btn()} onClick={addRubric} type="button"><Plus size={14}/> Add</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {form.rubric.map((r,i)=>(
            <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">
              {r}<button onClick={()=>removeRubric(i)}><X size={10}/></button>
            </span>
          ))}
        </div>
      </F>
      <div className="flex justify-end gap-3 mt-2">
        <button className={btn('outline')} onClick={onClose}>Cancel</button>
        <button className={btn()} onClick={submit} disabled={saving}>{saving?'Saving…':'Create Template'}</button>
      </div>
    </Modal>
  );
}

// ── Score Entry Table ──────────────────────────────────────────
function ScoreEntryTab({ programme }) {
  const [scores,  setScores]  = useState([]);
  const [edits,   setEdits]   = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [filter,  setFilter]  = useState('');

  const load = useCallback(() => {
    if (!programme) return;
    setLoading(true);
    enrichmentAPI.getScores(programme.id)
      .then(r => { setScores(r.data); setEdits({}); })
      .catch(() => toast.error('Failed to load scores'))
      .finally(() => setLoading(false));
  }, [programme]);

  useEffect(() => { load(); }, [load]);

  const setEdit = (sid, field, val) => setEdits(p=>({ ...p, [sid]: { ...(p[sid]||{}), [field]: val } }));
  const getVal  = (row, field) => edits[row.student_id]?.[field] ?? row[field] ?? '';

  const saveAll = async () => {
    const changed = scores.filter(r => edits[r.student_id]);
    if (!changed.length) return toast('No changes to save');
    setSaving(true);
    try {
      const payload = changed.map(r => ({
        student_id: r.student_id,
        baseline_marks: parseFloat(getVal(r,'baseline_marks'))||null,
        summative_marks: parseFloat(getVal(r,'summative_marks'))||null,
        ongoing_progress_score: parseFloat(getVal(r,'ongoing_progress_score'))||null,
        remarks: getVal(r,'remarks')||null,
      }));
      await enrichmentAPI.bulkSaveScores(programme.id, { scores: payload });
      toast.success('All scores saved');
      load();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    setSaving(false);
  };

  const filtered = scores.filter(s => !filter || s.student_name?.toLowerCase().includes(filter.toLowerCase()) || s.class_name?.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input className={`${inp} pl-8 w-64`} placeholder="Filter students…" value={filter} onChange={e=>setFilter(e.target.value)}/>
        </div>
        <div className="flex gap-2">
          <button className={btn('outline')} onClick={load}><RefreshCw size={13}/> Refresh</button>
          <button className={btn('green')} onClick={saveAll} disabled={saving || !Object.keys(edits).length}>
            {saving?<Loader2 size={13} className="animate-spin"/>:<CheckCheck size={13}/>}
            {saving?'Saving…':'Save All'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-gray-400 gap-2"><Loader2 size={20} className="animate-spin"/> Loading…</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-8">#</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Student</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Class</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Baseline</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Summative<br/><span className="text-gray-400 normal-case font-normal">(40%)</span></th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Ongoing<br/><span className="text-gray-400 normal-case font-normal">(60%)</span></th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Total</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Growth %</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Level</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => {
                const changed = !!edits[row.student_id];
                const bVal = parseFloat(getVal(row,'baseline_marks'));
                const sVal = parseFloat(getVal(row,'summative_marks'));
                const oVal = parseFloat(getVal(row,'ongoing_progress_score'));
                const maxM = parseFloat(row.max_marks)||100;
                let liveTotal = row.total_marks;
                let liveGrowth = row.growth_pct;
                let liveLevel = row.performance_level;
                if (!isNaN(bVal) && !isNaN(sVal) && !isNaN(oVal)) {
                  liveTotal = Math.round(((sVal*0.4)+(oVal*0.6))*100)/100;
                  const gap = maxM - bVal;
                  liveGrowth = gap <= 0 ? 0 : Math.round(((liveTotal - bVal) / gap) * 10000) / 100;
                  liveLevel = growthLevel(liveGrowth);
                }
                return (
                  <tr key={row.student_id} className={`border-t border-gray-50 hover:bg-gray-50/50 ${changed?'bg-blue-50/30':''}`}>
                    <td className="px-3 py-2 text-gray-400 text-xs">{i+1}</td>
                    <td className="px-3 py-2 font-semibold text-gray-900">{row.student_name}</td>
                    <td className="px-3 py-2 text-gray-500">{row.class_name}</td>
                    {['baseline_marks','summative_marks','ongoing_progress_score'].map(field=>(
                      <td key={field} className="px-2 py-1.5 text-center">
                        <input
                          type="number" min={0} max={maxM} step={0.5}
                          className="w-16 text-center border border-gray-200 rounded-lg px-1 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                          value={getVal(row, field)}
                          onChange={e=>setEdit(row.student_id, field, e.target.value)}
                          placeholder="—"
                        />
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center font-bold text-gray-700">{liveTotal??'—'}</td>
                    <td className="px-3 py-2"><GrowthBar pct={liveGrowth}/></td>
                    <td className="px-3 py-2"><PerfBadge level={liveLevel} small/></td>
                    <td className="px-2 py-1.5">
                      <input className="w-32 border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-300"
                        value={getVal(row,'remarks')} onChange={e=>setEdit(row.student_id,'remarks',e.target.value)} placeholder="Remarks…"/>
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && <tr><td colSpan={10} className="text-center py-10 text-gray-400">No students enrolled in this programme</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-400 mt-3">Total = (Summative × 40%) + (Ongoing × 60%) · Growth = (Total − Baseline) ÷ (Max − Baseline) × 100</p>
    </div>
  );
}

// ── Analytics Tab ──────────────────────────────────────────────
function AnalyticsTab({ programme }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!programme) return;
    setLoading(true);
    enrichmentAPI.getAnalytics(programme.id)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [programme]);

  if (!programme) return <div className="py-16 text-center text-gray-400">Select a programme</div>;
  if (loading) return <div className="flex justify-center py-16 text-gray-400 gap-2"><Loader2 size={20} className="animate-spin"/> Loading…</div>;
  if (!data) return null;

  const { kpis, gradePerf, perfDist, topStudents, atRisk, attTrend } = data;

  const attPct = kpis.avg_attendance ?? 0;
  const completionPct = kpis.total_students > 0 ? Math.round((kpis.scores_completed / kpis.total_students)*100) : 0;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Total Students"      value={kpis.total_students}  icon={Users}       color="blue"/>
        <KPICard label="Avg Growth %"         value={kpis.avg_growth!=null?`${kpis.avg_growth}%`:'—'} icon={TrendingUp} color="emerald"/>
        <KPICard label="Avg Attendance"       value={`${attPct}%`}         icon={UserCheck}   color={attPct<75?'red':'blue'}/>
        <KPICard label="Score Completion"     value={`${completionPct}%`} icon={CheckCircle} color="purple"/>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Total Sessions"       value={kpis.total_sessions}  icon={Calendar}    color="amber"/>
        <KPICard label="Outstanding Students" value={kpis.outstanding}     icon={Star}        color="emerald"/>
        <KPICard label="At Risk Students"     value={kpis.at_risk}         icon={AlertTriangle} color="red"/>
        <KPICard label="Avg Score"            value={kpis.avg_score!=null?`${kpis.avg_score}/${programme.max_marks||100}`:'—'} icon={Award} color="purple"/>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Grade Performance */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h4 className="font-bold text-gray-800 mb-4">Grade-wise Performance</h4>
          {gradePerf.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={gradePerf} barSize={40}>
                <XAxis dataKey="grade" tick={{fontSize:11}}/>
                <YAxis domain={[0,100]} tick={{fontSize:11}} tickCount={6}/>
                <Tooltip formatter={(v,n)=>[`${v}${n==='avg_growth'?'%':' marks'}`, n==='avg_growth'?'Avg Growth':'Avg Score']}/>
                <Legend iconType="circle" iconSize={8}/>
                <Bar dataKey="avg_growth" name="Growth %" fill="#3b82f6" radius={[4,4,0,0]}/>
                <Bar dataKey="avg_score"  name="Avg Score" fill="#10b981" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-gray-400 py-10">No grade data yet</p>}
        </div>

        {/* Performance Distribution Pie */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h4 className="font-bold text-gray-800 mb-4">Performance Distribution</h4>
          {perfDist.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={perfDist} dataKey="count" nameKey="level" cx="50%" cy="50%" outerRadius={90} label={({level,percent})=>`${level} (${(percent*100).toFixed(0)}%)`} labelLine={false} fontSize={10}>
                  {perfDist.map((e,i)=>(
                    <Cell key={i} fill={perfColor(e.level).bar}/>
                  ))}
                </Pie>
                <Tooltip/>
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-gray-400 py-10">No assessments completed yet</p>}
        </div>
      </div>

      {/* Attendance Trend */}
      {attTrend.length > 1 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h4 className="font-bold text-gray-800 mb-4">Attendance Trend</h4>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={attTrend}>
              <XAxis dataKey="session_date" tick={{fontSize:10}}/>
              <YAxis domain={[0,100]} tick={{fontSize:11}} tickCount={6}/>
              <Tooltip formatter={v=>[`${v}%`,'Attendance']}/>
              <Line type="monotone" dataKey="pct" stroke="#3b82f6" strokeWidth={2} dot={{r:4}} name="Attendance %"/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Performers & At Risk */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2"><Star size={15} className="text-amber-400"/> Top Performers</h4>
          {topStudents.length ? (
            <div className="space-y-2">
              {topStudents.map((s,i)=>(
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50">
                  <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">{i+1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{s.student_name}</p>
                    <p className="text-xs text-gray-500">{s.class_name}</p>
                  </div>
                  <GrowthBar pct={s.growth_pct}/>
                  <PerfBadge level={s.performance_level} small/>
                </div>
              ))}
            </div>
          ) : <p className="text-center text-gray-400 py-6 text-sm">No scores recorded yet</p>}
        </div>

        <div className="bg-white rounded-2xl border border-red-50 shadow-sm p-5">
          <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2"><AlertTriangle size={15} className="text-red-500"/> Students Needing Intervention</h4>
          {atRisk.length ? (
            <div className="space-y-2">
              {atRisk.map((s,i)=>(
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl bg-red-50/50">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{s.student_name}</p>
                    <p className="text-xs text-gray-500">{s.class_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Growth</p>
                    <p className="text-sm font-bold text-red-600">{s.growth_pct!=null?`${s.growth_pct}%`:'—'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Att.</p>
                    <p className={`text-sm font-bold ${(s.attendance_pct??100)<75?'text-red-600':'text-gray-600'}`}>{s.attendance_pct!=null?`${s.attendance_pct}%`:'—'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-center text-emerald-600 py-6 text-sm font-medium">All students on track</p>}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
const TABS = ['Overview','Programmes','Enrollment','Attendance','Assessments','Scores','Analytics'];

export default function EnrichmentProgramme() {
  const { yearId } = useContext(YearContext) || { yearId: 3 };

  const [tab,         setTab]         = useState(0);
  const [programmes,  setProgrammes]  = useState([]);
  const [employees,   setEmployees]   = useState([]);
  const [grades,      setGrades]      = useState([]);
  const [dashboard,   setDashboard]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [selectedProg,setSelectedProg]= useState(null);

  // Enrollment tab
  const [enrollments, setEnrollments] = useState([]);
  const [showEnroll,  setShowEnroll]  = useState(false);

  // Attendance tab
  const [sessions,     setSessions]     = useState([]);
  const [attSummary,   setAttSummary]   = useState([]);
  const [attModal,     setAttModal]     = useState(null);
  const [sessionForm,  setSessionForm]  = useState({ date:'', topic:'' });
  const [showSessionF, setShowSessionF] = useState(false);

  // Assessments tab
  const [templates,    setTemplates]    = useState([]);
  const [showTmplModal,setShowTmplModal]= useState(false);

  // Modals
  const [progModal, setProgModal] = useState(null); // null | 'create' | prog obj

  // ── Loaders ──────────────────────────────────────────────
  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, eRes, gRes, dRes] = await Promise.all([
        enrichmentAPI.getProgrammes(yearId),
        enrichmentAPI.getEmployees(),
        enrichmentAPI.getGrades(yearId),
        enrichmentAPI.getDashboard(yearId),
      ]);
      setProgrammes(pRes.data);
      setEmployees(eRes.data);
      setGrades(gRes.data);
      setDashboard(dRes.data);
      if (!selectedProg && pRes.data.length) setSelectedProg(pRes.data[0]);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, [yearId]);

  useEffect(() => { loadBase(); }, [loadBase]);

  const loadEnrollments = useCallback(() => {
    if (!selectedProg) return;
    enrichmentAPI.getEnrollments(selectedProg.id).then(r=>setEnrollments(r.data)).catch(()=>{});
  }, [selectedProg]);

  const loadSessions = useCallback(() => {
    if (!selectedProg) return;
    Promise.all([
      enrichmentAPI.getSessions(selectedProg.id),
      enrichmentAPI.getAttSummary(selectedProg.id),
    ]).then(([s,a])=>{ setSessions(s.data); setAttSummary(a.data); }).catch(()=>{});
  }, [selectedProg]);

  const loadTemplates = useCallback(() => {
    if (!selectedProg) return;
    enrichmentAPI.getTemplates(selectedProg.id).then(r=>setTemplates(r.data)).catch(()=>{});
  }, [selectedProg]);

  useEffect(() => {
    if (tab===2) loadEnrollments();
    if (tab===3) loadSessions();
    if (tab===4) loadTemplates();
  }, [tab, loadEnrollments, loadSessions, loadTemplates]);

  // ── Programme actions ────────────────────────────────────
  const deleteProg = async (p) => {
    if (!window.confirm(`Delete "${p.name}" and all data? This cannot be undone.`)) return;
    try { await enrichmentAPI.deleteProgramme(p.id); toast.success('Deleted'); loadBase(); if (selectedProg?.id===p.id) setSelectedProg(null); }
    catch(e) { toast.error(e.response?.data?.error||'Failed'); }
  };

  const createSession = async () => {
    if (!sessionForm.date) return toast.error('Session date required');
    try {
      await enrichmentAPI.createSession(selectedProg.id, { session_date: sessionForm.date, topic: sessionForm.topic });
      toast.success('Session added');
      setShowSessionF(false); setSessionForm({ date:'', topic:'' });
      loadSessions();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm('Delete this session and its attendance?')) return;
    try { await enrichmentAPI.deleteSession(selectedProg.id, sid); toast.success('Deleted'); loadSessions(); }
    catch { toast.error('Failed'); }
  };

  const deleteTemplate = async (tid) => {
    if (!window.confirm('Delete this template?')) return;
    try { await enrichmentAPI.deleteTemplate(selectedProg.id, tid); toast.success('Deleted'); loadTemplates(); }
    catch { toast.error('Failed'); }
  };

  // ── Derived data ─────────────────────────────────────────
  const totalStudents  = dashboard?.totals?.total_students ?? programmes.reduce((s,p)=>s+(p.enrolled_count||0),0);
  const totalProgrammes= programmes.length;
  const overallGrowth  = programmes.length ? Math.round(programmes.filter(p=>p.avg_growth!=null).reduce((s,p)=>s+(p.avg_growth||0),0)/Math.max(1,programmes.filter(p=>p.avg_growth!=null).length)*10)/10 : 0;
  const atRiskTotal    = dashboard?.atRiskAll?.length ?? 0;
  const lowAttTotal    = dashboard?.lowAtt?.length ?? 0;

  const STATUS_COLORS  = { active:'bg-emerald-100 text-emerald-700', inactive:'bg-gray-100 text-gray-500', completed:'bg-blue-100 text-blue-700' };

  return (
    <div className="p-6 space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2"><Award className="text-blue-600" size={26}/> Enrichment Programme</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage programmes, enrollment, attendance & assessments</p>
        </div>
        <div className="flex items-center gap-2">
          <button className={btn('outline')} onClick={loadBase}><RefreshCw size={13}/> Refresh</button>
          <button className={btn()} onClick={()=>setProgModal('create')}><Plus size={14}/> New Programme</button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
        {TABS.map((t,i)=>(
          <button key={t} onClick={()=>setTab(i)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${tab===i?'bg-white text-blue-700 shadow-sm':'text-gray-600 hover:text-gray-900'}`}>
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400 gap-3"><Loader2 size={24} className="animate-spin"/> Loading Enrichment Data…</div>
      ) : (

      <>
      {/* ── Tab 0: Overview ─────────────────────────────── */}
      {tab===0 && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard label="Total Programmes"   value={totalProgrammes}     icon={Award}         color="blue"/>
            <KPICard label="Total Students"     value={totalStudents}       icon={Users}         color="emerald"/>
            <KPICard label="Avg Growth"         value={overallGrowth?`${overallGrowth}%`:'—'}    icon={TrendingUp} color="purple"/>
            <KPICard label="At Risk Students"   value={atRiskTotal}         icon={AlertTriangle} color="red"/>
          </div>

          {/* Programme Rankings */}
          {dashboard?.programmes?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2"><BarChart2 size={16} className="text-blue-500"/> Programme Rankings</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      {['#','Programme','Category','Students','Avg Growth','Status'].map(h=>(
                        <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.programmes.map((p,i)=>(
                      <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer" onClick={()=>{setSelectedProg(p);setTab(6);}}>
                        <td className="px-3 py-2.5 text-gray-400 font-mono text-xs">{i+1}</td>
                        <td className="px-3 py-2.5 font-semibold text-gray-900">{p.name}</td>
                        <td className="px-3 py-2.5 text-gray-500">{p.category}</td>
                        <td className="px-3 py-2.5 text-center">{p.enrolled}</td>
                        <td className="px-3 py-2.5"><GrowthBar pct={p.avg_growth}/></td>
                        <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[p.status]||'bg-gray-100 text-gray-500'}`}>{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Alerts */}
          {(atRiskTotal>0||lowAttTotal>0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {dashboard?.atRiskAll?.length > 0 && (
                <div className="bg-red-50 border border-red-100 rounded-2xl p-5">
                  <h4 className="font-bold text-red-800 flex items-center gap-2 mb-3"><AlertTriangle size={15}/> Low Growth Alert ({dashboard.atRiskAll.length})</h4>
                  <div className="space-y-1.5">
                    {dashboard.atRiskAll.slice(0,6).map((s,i)=>(
                      <div key={i} className="flex items-center justify-between bg-white/70 rounded-lg px-3 py-2">
                        <div><p className="text-sm font-medium text-gray-900">{s.student_name}</p><p className="text-xs text-gray-500">{s.class_name} · {s.programme_name}</p></div>
                        <span className="text-sm font-bold text-red-600">{s.growth_pct!=null?`${s.growth_pct}%`:'Pending'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {dashboard?.lowAtt?.length > 0 && (
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
                  <h4 className="font-bold text-amber-800 flex items-center gap-2 mb-3"><Clock size={15}/> Low Attendance Alert ({dashboard.lowAtt.length})</h4>
                  <div className="space-y-1.5">
                    {dashboard.lowAtt.slice(0,6).map((s,i)=>(
                      <div key={i} className="flex items-center justify-between bg-white/70 rounded-lg px-3 py-2">
                        <div><p className="text-sm font-medium text-gray-900">{s.student_name}</p><p className="text-xs text-gray-500">{s.class_name} · {s.programme_name}</p></div>
                        <span className="text-sm font-bold text-amber-600">{s.attendance_pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {programmes.length===0 && (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
              <Award size={40} className="mx-auto mb-3 text-gray-300"/>
              <p className="font-semibold text-gray-500">No programmes yet</p>
              <p className="text-sm text-gray-400 mt-1">Create your first Enrichment Programme to get started</p>
              <button className={`${btn()} mt-4`} onClick={()=>setProgModal('create')}><Plus size={14}/> New Programme</button>
            </div>
          )}
        </div>
      )}

      {/* ── Tab 1: Programmes ────────────────────────────── */}
      {tab===1 && (
        <div className="space-y-4">
          {programmes.map(p=>{
            const grades_ = Array.isArray(p.target_grades)?p.target_grades:(()=>{try{return JSON.parse(p.target_grades||'[]')}catch{return []}})();
            return (
              <div key={p.id} className={`bg-white rounded-2xl border shadow-sm p-5 hover:shadow-md transition-shadow ${selectedProg?.id===p.id?'border-blue-300 ring-1 ring-blue-200':'border-gray-100'}`}>
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={()=>setSelectedProg(p)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900">{p.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[p.status]||''}`}>{p.status}</span>
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{p.category}</span>
                    </div>
                    {p.subject && <p className="text-sm text-gray-500 mt-0.5">Subject: <strong>{p.subject}</strong>{p.teacher_name?` · Teacher: ${p.teacher_name}`:''}</p>}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 flex-wrap">
                      {p.start_date && <span>📅 {p.start_date?.slice(0,10)} → {p.end_date?.slice(0,10)||'…'}</span>}
                      <span><Users size={11} className="inline mr-1"/>{p.enrolled_count||0} enrolled</span>
                      {p.avg_growth!=null && <span className="text-blue-600 font-semibold">↑ {p.avg_growth}% avg growth</span>}
                      {grades_.length>0 && grades_.map(g=><span key={g} className="bg-gray-100 px-1.5 rounded text-xs">{g}</span>)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button className={btn('outline')} onClick={()=>{setSelectedProg(p);setTab(6);}}><BarChart2 size={13}/> Analytics</button>
                    <button className={btn('outline')} onClick={()=>setProgModal(p)}><Edit2 size={13}/></button>
                    <button className={btn('danger')} onClick={()=>deleteProg(p)}><Trash2 size={13}/></button>
                  </div>
                </div>
              </div>
            );
          })}
          {!programmes.length && (
            <div className="text-center py-16 text-gray-400">
              <Award size={40} className="mx-auto mb-3 opacity-30"/>
              <p className="font-medium">No programmes this year</p>
              <button className={`${btn()} mt-4`} onClick={()=>setProgModal('create')}><Plus size={14}/> Create First Programme</button>
            </div>
          )}
        </div>
      )}

      {/* ── Tab 2: Enrollment ────────────────────────────── */}
      {tab===2 && (
        <div>
          <ProgSelector programmes={programmes} selected={selectedProg} onSelect={p=>{setSelectedProg(p);}} onLoad={loadEnrollments}/>
          {selectedProg && (
            <>
              <div className="flex items-center justify-between mb-4 mt-4">
                <p className="text-sm text-gray-500">{enrollments.length} students enrolled in <strong>{selectedProg.name}</strong></p>
                <button className={btn()} onClick={()=>setShowEnroll(true)}><Plus size={14}/> Enroll Students</button>
              </div>
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['#','Student','Class','Grade','Attendance','Growth','Level','Action'].map(h=>(
                        <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.map((e,i)=>(
                      <tr key={e.id} className="border-t border-gray-50 hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-400 text-xs">{i+1}</td>
                        <td className="px-3 py-2 font-semibold text-gray-900">{e.student_name}<br/><span className="text-xs text-gray-400">{e.admission_no}</span></td>
                        <td className="px-3 py-2 text-gray-500">{e.class_name}</td>
                        <td className="px-3 py-2 text-gray-500">{e.grade}</td>
                        <td className="px-3 py-2">
                          {e.attendance_pct!=null?(
                            <span className={`text-xs font-bold ${e.attendance_pct<75?'text-red-600':'text-emerald-600'}`}>{e.attendance_pct}%<br/><span className="font-normal text-gray-400">{e.classes_attended}/{e.classes_held} classes</span></span>
                          ):'—'}
                        </td>
                        <td className="px-3 py-2"><GrowthBar pct={e.growth_pct}/></td>
                        <td className="px-3 py-2"><PerfBadge level={e.performance_level} small/></td>
                        <td className="px-3 py-2">
                          <button className="text-red-400 hover:text-red-600 p-1" onClick={async()=>{ if(!window.confirm('Remove student?')) return; await enrichmentAPI.removeEnroll(selectedProg.id,e.id); toast.success('Removed'); loadEnrollments(); }}><Trash2 size={14}/></button>
                        </td>
                      </tr>
                    ))}
                    {!enrollments.length && <tr><td colSpan={8} className="text-center py-10 text-gray-400">No students enrolled yet</td></tr>}
                  </tbody>
                </table>
              </div>
              {showEnroll && <EnrollModal programme={selectedProg} employees={employees} yearId={yearId} onSave={()=>{setShowEnroll(false);loadEnrollments();}} onClose={()=>setShowEnroll(false)}/>}
            </>
          )}
        </div>
      )}

      {/* ── Tab 3: Attendance ────────────────────────────── */}
      {tab===3 && (
        <div>
          <ProgSelector programmes={programmes} selected={selectedProg} onSelect={p=>{setSelectedProg(p);}} onLoad={loadSessions}/>
          {selectedProg && (
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sessions list */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-gray-800">Sessions ({sessions.length})</h4>
                  <button className={btn()} onClick={()=>setShowSessionF(true)}><Plus size={13}/> Add Session</button>
                </div>
                {showSessionF && (
                  <div className="bg-blue-50 rounded-xl p-4 mb-3 flex gap-3 items-end">
                    <div className="flex-1"><SL>Date</SL><input type="date" className={inp} value={sessionForm.date} onChange={e=>setSessionForm(p=>({...p,date:e.target.value}))}/></div>
                    <div className="flex-1"><SL>Topic (optional)</SL><input className={inp} value={sessionForm.topic} onChange={e=>setSessionForm(p=>({...p,topic:e.target.value}))}/></div>
                    <button className={btn('green')} onClick={createSession}><CheckCircle size={13}/> Add</button>
                    <button className={btn('outline')} onClick={()=>setShowSessionF(false)}>Cancel</button>
                  </div>
                )}
                <div className="space-y-2">
                  {sessions.map(s=>(
                    <div key={s.id} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center justify-between shadow-sm">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{s.session_date}</p>
                        {s.topic && <p className="text-xs text-gray-500">{s.topic}</p>}
                        <p className="text-xs text-gray-400 mt-0.5">{s.present_count||0}/{s.total_students||0} present</p>
                      </div>
                      <div className="flex gap-2">
                        <button className={btn('outline')} onClick={()=>setAttModal(s)}><UserCheck size={13}/> Mark</button>
                        <button className={btn('danger')} onClick={()=>deleteSession(s.id)}><Trash2 size={13}/></button>
                      </div>
                    </div>
                  ))}
                  {!sessions.length && <p className="text-center py-8 text-gray-400 text-sm">No sessions yet</p>}
                </div>
              </div>

              {/* Attendance summary */}
              <div>
                <h4 className="font-bold text-gray-800 mb-3">Student Attendance Summary</h4>
                <div className="overflow-auto max-h-[500px] rounded-xl border border-gray-100">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        {['Student','Class','Held','Attended','%'].map(h=>(
                          <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {attSummary.map((a,i)=>(
                        <tr key={i} className={`border-t border-gray-50 ${(a.attendance_pct??100)<75?'bg-red-50/30':''}`}>
                          <td className="px-3 py-2 font-medium text-gray-900">{a.student_name}</td>
                          <td className="px-3 py-2 text-gray-500">{a.class_name}</td>
                          <td className="px-3 py-2 text-center">{a.classes_held}</td>
                          <td className="px-3 py-2 text-center">{a.classes_attended}</td>
                          <td className="px-3 py-2">
                            <span className={`text-sm font-bold ${(a.attendance_pct??100)<75?'text-red-600':'text-emerald-600'}`}>{a.attendance_pct??'—'}%</span>
                            {(a.attendance_pct??100)<75 && <span className="ml-1 text-xs text-red-500">⚠</span>}
                          </td>
                        </tr>
                      ))}
                      {!attSummary.length && <tr><td colSpan={5} className="text-center py-8 text-gray-400">No data</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {attModal && <AttendanceModal session={attModal} programmeId={selectedProg?.id} onSave={()=>{setAttModal(null);loadSessions();}} onClose={()=>setAttModal(null)}/>}
        </div>
      )}

      {/* ── Tab 4: Assessments (Templates) ───────────────── */}
      {tab===4 && (
        <div>
          <ProgSelector programmes={programmes} selected={selectedProg} onSelect={p=>{setSelectedProg(p);}} onLoad={loadTemplates}/>
          {selectedProg && (
            <>
              <div className="flex items-center justify-between mt-4 mb-3">
                <h4 className="font-bold text-gray-800">Assessment Templates</h4>
                <button className={btn()} onClick={()=>setShowTmplModal(true)}><Plus size={14}/> New Template</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(t=>{
                  const rubric = Array.isArray(t.rubric)?t.rubric:(()=>{try{return JSON.parse(t.rubric||'[]')}catch{return []}})();
                  const typeColor = { baseline:'bg-purple-50 text-purple-700', formative:'bg-blue-50 text-blue-700', summative:'bg-amber-50 text-amber-700', final:'bg-emerald-50 text-emerald-700' };
                  return (
                    <div key={t.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-1 inline-block ${typeColor[t.assessment_type]||'bg-gray-100 text-gray-500'}`}>{t.assessment_type}</span>
                        </div>
                        <button className={btn('danger')} onClick={()=>deleteTemplate(t.id)}><Trash2 size={13}/></button>
                      </div>
                      <div className="mt-3 space-y-1 text-xs text-gray-500">
                        <p>Max Marks: <strong>{t.max_marks}</strong></p>
                        {t.grade && <p>Grade: <strong>{t.grade}</strong></p>}
                        {t.assessment_date && <p>Date: <strong>{t.assessment_date.slice(0,10)}</strong></p>}
                      </div>
                      {rubric.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {rubric.map((r,i)=>(
                            <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{r}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {!templates.length && (
                  <div className="col-span-3 text-center py-12 text-gray-400">
                    <Clipboard size={36} className="mx-auto mb-2 opacity-40"/>
                    <p className="text-sm">No assessment templates yet</p>
                    <p className="text-xs mt-1">Create a Baseline template first, then Summative</p>
                  </div>
                )}
              </div>
              {showTmplModal && <TemplateModal programmeId={selectedProg.id} grades={grades} onSave={()=>{setShowTmplModal(false);loadTemplates();}} onClose={()=>setShowTmplModal(false)}/>}
            </>
          )}
        </div>
      )}

      {/* ── Tab 5: Scores ────────────────────────────────── */}
      {tab===5 && (
        <div>
          <ProgSelector programmes={programmes} selected={selectedProg} onSelect={p=>setSelectedProg(p)} onLoad={()=>{}}/>
          {selectedProg && <div className="mt-4"><ScoreEntryTab programme={selectedProg}/></div>}
        </div>
      )}

      {/* ── Tab 6: Analytics ─────────────────────────────── */}
      {tab===6 && (
        <div>
          <ProgSelector programmes={programmes} selected={selectedProg} onSelect={p=>setSelectedProg(p)} onLoad={()=>{}}/>
          {selectedProg && <div className="mt-4"><AnalyticsTab programme={selectedProg}/></div>}
        </div>
      )}
      </>
      )}

      {/* Programme Create/Edit Modal */}
      {progModal && (
        <ProgrammeModal
          prog={progModal==='create'?null:progModal}
          employees={employees}
          grades={grades}
          yearId={yearId}
          onSave={()=>{ setProgModal(null); loadBase(); }}
          onClose={()=>setProgModal(null)}
        />
      )}
    </div>
  );
}

// ── Programme Selector Dropdown ───────────────────────────────
function ProgSelector({ programmes, selected, onSelect, onLoad }) {
  if (!programmes.length) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700 font-medium">
      No programmes found. Create one in the Programmes tab first.
    </div>
  );
  return (
    <div className="flex items-center gap-3">
      <label className="text-sm font-semibold text-gray-600 whitespace-nowrap">Programme:</label>
      <select
        className="flex-1 max-w-sm border border-gray-200 rounded-xl px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-400"
        value={selected?.id||''}
        onChange={e => {
          const p = programmes.find(x=>String(x.id)===e.target.value);
          if (p) { onSelect(p); setTimeout(onLoad, 50); }
        }}
      >
        {programmes.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {selected && (
        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-medium">{selected.enrolled_count||0} students</span>
      )}
    </div>
  );
}
